import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { normalizeWorkflowIconKeyForUi } from '../../shared/utils/workflow-icon-key.util';
import type {
  CardApiContract,
  CardQuickFieldApiContract,
  ProjectApiContract,
  WorkflowApiContract,
  WorkflowStageApiContract,
} from './contracts/operations.contract';

/** Normaliza para comparar chaves/rótulos (ex.: "Descrição" → "descricao"). */
function normalizeAsciiLower(raw: string): string {
  return raw
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .trim();
}

type JsonRecord = Record<string, unknown>;

function asRecord(value: unknown): JsonRecord {
  return value && typeof value === 'object' ? (value as JsonRecord) : {};
}

function asArray(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  const body = asRecord(value);
  const items = body['items'];
  return Array.isArray(items) ? items : [];
}

/** Resposta de GET cards por workflow: `items`, `cards`, `data`, etc. */
function asCardsWorkflowListArray(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  const root = asRecord(value);
  const keys = ['items', 'Items', 'cards', 'Cards', 'data', 'Data', 'results', 'Results'] as const;
  for (const key of keys) {
    const v = root[key];
    if (Array.isArray(v)) return v;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const inner = asRecord(v);
      for (const ik of ['items', 'Items', 'cards', 'Cards'] as const) {
        const a = inner[ik];
        if (Array.isArray(a)) return a;
      }
    }
  }
  return asArray(value);
}

/** GET /api/workflow-fields/workflow/:id — a API pode devolver array cru, `items`, `entries`, `fields` ou dentro de `data`. */
function unwrapWorkflowFieldsResponse(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  const root = asRecord(raw);
  const keys = ['items', 'Items', 'entries', 'Entries', 'fields', 'Fields'] as const;
  for (const key of keys) {
    const v = root[key];
    if (Array.isArray(v)) return v;
  }
  const data = asRecord(root['data'] ?? root['Data']);
  for (const key of keys) {
    const v = data[key];
    if (Array.isArray(v)) return v;
  }
  return [];
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNullableString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' ? value : fallback;
}

function asNullableNumber(value: unknown): number | null {
  return typeof value === 'number' ? value : null;
}

function firstNonEmptyString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (trimmed) return trimmed;
  }
  return '';
}

/** Lê id de entidades .NET / envelopes (`data`, `card`, PascalCase). */
function readEntityId(source: JsonRecord): string {
  const keys = ['id', 'Id', 'cardId', 'CardId'] as const;
  for (const key of keys) {
    const v = source[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(Math.trunc(v));
  }
  return '';
}

function readQuickFieldsArray(source: JsonRecord): unknown[] {
  const keys = [
    'quickFields',
    'QuickFields',
    'quickViewFields',
    'QuickViewFields',
    'cardQuickFields',
    'CardQuickFields',
    'displayFields',
    'DisplayFields',
  ] as const;
  for (const key of keys) {
    const v = source[key];
    if (Array.isArray(v) && v.length > 0) return v;
  }
  for (const key of keys) {
    const v = source[key];
    if (Array.isArray(v)) return v;
  }
  return [];
}

/**
 * Extrai valores brutos de campos personalizados do payload do card, cobrindo os formatos que o
 * backend usa: bags `customFields` / `customFieldValues` / `workflowFieldValues` e arrays
 * `fieldValues` / `cardFieldValues`. As chaves preservam o formato de origem (UUID, key, label) —
 * a normalização final fica no consumidor, que tem acesso ao schema do workflow.
 */
function extractCardFieldValues(
  source: JsonRecord
): Record<string, string | number | boolean | string[] | null> {
  const out: Record<string, string | number | boolean | string[] | null> = {};

  const bagKeys = [
    'customFields',
    'CustomFields',
    'customFieldValues',
    'CustomFieldValues',
    'workflowFieldValues',
    'WorkflowFieldValues',
  ] as const;
  for (const bk of bagKeys) {
    const bag = source[bk];
    if (!bag || typeof bag !== 'object' || Array.isArray(bag)) continue;
    for (const [key, raw] of Object.entries(bag as JsonRecord)) {
      if (!key) continue;
      if (out[key] != null) continue;
      if (Array.isArray(raw)) {
        out[key] = raw.map((item) => String(item));
        continue;
      }
      const scalar = coerceQuickFieldScalar(raw);
      if (scalar != null) out[key] = scalar;
    }
  }

  const arrayKeys = ['fieldValues', 'FieldValues', 'cardFieldValues', 'CardFieldValues'] as const;
  for (const ak of arrayKeys) {
    const arr = source[ak];
    if (!Array.isArray(arr)) continue;
    for (const item of arr) {
      const r = asRecord(item);
      const value = coerceQuickFieldScalar(r['value'] ?? r['Value']);
      if (value == null) continue;
      const ids = [
        firstNonEmptyString(r['workflowFieldId'], r['WorkflowFieldId']),
        firstNonEmptyString(r['fieldId'], r['FieldId']),
        firstNonEmptyString(r['key'], r['Key'], r['fieldKey'], r['FieldKey']),
        firstNonEmptyString(r['label'], r['Label']),
      ].filter((s) => s.length);
      for (const id of ids) {
        if (out[id] == null) out[id] = value;
      }
    }
  }

  return out;
}

/** Valor escalar para quick field (API às vezes envia objeto `{ value, text }`). */
function coerceQuickFieldScalar(raw: unknown): string | number | boolean | null {
  if (raw == null) return null;
  if (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') return raw;
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const r = raw as JsonRecord;
    const nested = r['value'] ?? r['Value'] ?? r['text'] ?? r['Text'] ?? r['displayValue'] ?? r['DisplayValue'];
    if (typeof nested === 'string' || typeof nested === 'number' || typeof nested === 'boolean') {
      return nested;
    }
    const s = r['stringValue'] ?? r['StringValue'] ?? r['content'] ?? r['Content'];
    if (typeof s === 'string') return s;
  }
  return null;
}

function unwrapCardPayload(raw: unknown, maxDepth = 4): JsonRecord {
  let current: JsonRecord = asRecord(raw);
  for (let d = 0; d < maxDepth; d++) {
    if (readEntityId(current)) return current;
    const inner =
      current['card'] ??
      current['Card'] ??
      current['data'] ??
      current['Data'] ??
      current['result'] ??
      current['Result'];
    if (!inner || typeof inner !== 'object' || Array.isArray(inner)) break;
    current = asRecord(inner);
  }
  return current;
}


@Injectable({ providedIn: 'root' })
export class OperationsApiService {
  async deleteCards(cardIds: string[]): Promise<void> {
    await firstValueFrom(this.http.post<void>('/api/cards/bulk-delete', { cardIds }));
  }

  private readonly http = inject(HttpClient);

  async getProjects(): Promise<ProjectApiContract[]> {
    const response = await this.getWithFallback(['/api/projects', '/projects']);
    return asArray(response)
      .map((item) => this.mapProject(item))
      .filter((item) => !!item.id && !!item.name);
  }

  async getWorkflows(): Promise<WorkflowApiContract[]> {
    const response = await this.getWithFallback(['/api/workflows', '/workflows']);
    const workflows = asArray(response).map((item) => this.mapWorkflowBase(item));

    // Sempre hidratar etapas pelo GET /workflows/{id}/stages: a listagem costuma vir
    // incompleta ou com snapshot defasado (ex.: workflow recém-criado com N etapas).
    const withStages = await Promise.all(
      workflows.map(async (workflow) => {
        try {
          const stages = await this.getWorkflowStages(workflow.id);
          return { ...workflow, stages };
        } catch {
          return workflow;
        }
      })
    );

    return withStages;
  }

  async getWorkflowStages(workflowId: string): Promise<WorkflowStageApiContract[]> {
    const response = await firstValueFrom(
      this.http.get<unknown>(`/api/workflows/${workflowId}/stages`)
    );
    return asArray(response)
      .map((item, index) => this.mapWorkflowStage(item, index))
      .sort((a, b) => a.order - b.order);
  }

  async getCardsByWorkflow(workflowId: string): Promise<CardApiContract[]> {
    const response = await firstValueFrom(
      this.http.get<unknown>(`/api/cards/workflow/${workflowId}`)
    );
    return asCardsWorkflowListArray(response).map((item) => this.mapCard(item));
  }

  async getCardById(cardId: string): Promise<JsonRecord> {
    const response = await firstValueFrom(this.http.get<unknown>(`/api/cards/${cardId}`));
    return asRecord(response);
  }

  /** Card no mesmo formato da listagem por workflow (incl. quickFields calculados). */
  async getBoardCardById(cardId: string): Promise<CardApiContract | null> {
    const response = await firstValueFrom(
      this.http.get<unknown>(`/api/cards/${encodeURIComponent(cardId)}`)
    );
    const mapped = this.mapCard(response);
    return mapped.id ? mapped : null;
  }

  async createCard(payload: JsonRecord): Promise<JsonRecord> {
    const response = await firstValueFrom(this.http.post<unknown>('/api/cards', payload));
    return unwrapCardPayload(response);
  }

  /** Id do card após POST /api/cards (resposta crua ou já normalizada). */
  parseCreatedCardId(response: unknown): string {
    return readEntityId(unwrapCardPayload(response));
  }

  /** Código do card após POST /api/cards, quando a API devolve no payload. */
  parseCreatedCardCode(response: unknown): string | null {
    const source = unwrapCardPayload(response);
    const raw = source['code'] ?? source['Code'];
    if (typeof raw === 'number' && Number.isFinite(raw)) return String(Math.trunc(raw));
    if (typeof raw === 'string') {
      const t = raw.trim();
      return t.length ? t : null;
    }
    return null;
  }

  async updateCard(cardId: string, payload: JsonRecord): Promise<JsonRecord> {
    const response = await firstValueFrom(this.http.put<unknown>(`/api/cards/${cardId}`, payload));
    return asRecord(response);
  }

  /**
   * PUT /api/cards/{id}/move — `MoveCardRequest` (camelCase):
   * cardId = {id} da rota, targetWorkflowStageId, targetPosition ≥ 1 (1-based; acima do tamanho da coluna a API faz clamp).
   */
  async moveCard(cardId: string, payload: JsonRecord): Promise<JsonRecord> {
    const trimmedCardId = cardId.trim();
    const targetWorkflowStageId = firstNonEmptyString(
      payload['targetWorkflowStageId'],
      payload['TargetWorkflowStageId'],
      payload['workflowStageId'],
      payload['WorkflowStageId']
    ).trim();
    if (!targetWorkflowStageId) {
      throw new Error('moveCard: missing targetWorkflowStageId');
    }
    const rawPos = payload['targetPosition'] ?? payload['TargetPosition'];
    const posNum =
      typeof rawPos === 'number'
        ? rawPos
        : typeof rawPos === 'string' && rawPos.trim()
          ? Number(rawPos.trim())
          : NaN;
    if (!Number.isFinite(posNum) || Math.trunc(posNum) < 1) {
      throw new Error('moveCard: targetPosition must be an integer >= 1');
    }
    const body: JsonRecord = {
      cardId: trimmedCardId,
      targetWorkflowStageId,
      targetPosition: Math.trunc(posNum),
    };
    const response = await firstValueFrom(
      this.http.put<unknown>(
        `/api/cards/${encodeURIComponent(trimmedCardId)}/move`,
        body
      )
    );
    return asRecord(response);
  }

  async getCardChecklists(cardId: string): Promise<unknown[]> {
    const response = await firstValueFrom(
      this.http.get<unknown>(`/api/cards/${cardId}/checklists`)
    );
    return asArray(response);
  }

  async getCardComments(cardId: string): Promise<unknown[]> {
    const response = await firstValueFrom(this.http.get<unknown>(`/api/cards/${cardId}/comments`));
    return asArray(response);
  }

  async getCardActivities(cardId: string): Promise<unknown[]> {
    const response = await firstValueFrom(
      this.http.get<unknown>(`/api/cards/${cardId}/activities`)
    );
    return asArray(response);
  }

  async getCardAttachments(cardId: string): Promise<unknown[]> {
    const response = await firstValueFrom(
      this.http.get<unknown>(`/api/cards/${cardId}/attachments`)
    );
    return asArray(response);
  }

  async getWorkflowFields(workflowId: string): Promise<unknown[]> {
    const response = await firstValueFrom(
      this.http.get<unknown>(`/api/workflow-fields/workflow/${workflowId}`)
    );
    return unwrapWorkflowFieldsResponse(response);
  }

  async getCardFieldValues(cardId: string): Promise<unknown[]> {
    const response = await firstValueFrom(
      this.http.get<unknown>(`/api/card-field-values/card/${cardId}`)
    );
    return asArray(response);
  }

  private mapProject(raw: unknown): ProjectApiContract {
    const source = asRecord(raw);
    const projectData = asRecord(source['project']);
    const id = firstNonEmptyString(source['id'], projectData['id']);
    const name = firstNonEmptyString(
      source['name'],
      source['title'],
      source['projectName'],
      source['nome'],
      projectData['name'],
      projectData['title'],
      projectData['nome'],
      id
    );
    return {
      id,
      name,
      description:
        asNullableString(source['description']) ??
        asNullableString(source['summary']) ??
        asNullableString(projectData['description']),
      coverImageUrl:
        asNullableString(source['coverImageUrl']) ??
        asNullableString(source['logoUrl']) ??
        asNullableString(source['assetUrl']) ??
        asNullableString(projectData['coverImageUrl']) ??
        asNullableString(projectData['logoUrl']) ??
        asNullableString(projectData['assetUrl']),
      iconKey:
        asString(source['iconKey']) ||
        asString(source['icon']) ||
        asString(projectData['iconKey']) ||
        asString(projectData['icon']) ||
        'folder',
      colorHex: asString(source['colorHex']) || asString(projectData['colorHex']) || '#3B82F6',
    };
  }

  private mapWorkflowBase(raw: unknown): WorkflowApiContract {
    const source = asRecord(raw);
    const workflowData = asRecord(source['workflow']);
    const id = firstNonEmptyString(source['id'], workflowData['id']);
    const name = firstNonEmptyString(
      source['name'],
      source['title'],
      source['workflowName'],
      source['listName'],
      source['nome'],
      workflowData['name'],
      workflowData['title'],
      workflowData['workflowName'],
      workflowData['listName'],
      workflowData['nome'],
      id
    );
    const projectId = firstNonEmptyString(
      source['projectId'],
      source['projectID'],
      source['spaceId'],
      workflowData['projectId'],
      workflowData['projectID'],
      workflowData['spaceId']
    );
    const stages = asArray(source['stages']).map((item, index) =>
      this.mapWorkflowStage(item, index)
    );

    return {
      id,
      projectId,
      name,
      iconKey: normalizeWorkflowIconKeyForUi(
        asString(source['iconKey']) || asString(workflowData['iconKey'], 'workflow')
      ),
      colorHex: asString(source['colorHex']) || asString(workflowData['colorHex'], '#3B82F6'),
      description: asNullableString(source['description']) ?? asNullableString(workflowData['description']),
      defaultDueInDays:
        asNullableNumber(source['defaultDueInDays']) ??
        asNullableNumber(source['default_due_in_days']) ??
        asNullableNumber(workflowData['defaultDueInDays']) ??
        asNullableNumber(workflowData['default_due_in_days']),
      isAccessRestricted:
        source['isAccessRestricted'] === true ||
        source['is_access_restricted'] === true ||
        workflowData['isAccessRestricted'] === true ||
        workflowData['is_access_restricted'] === true,
      stages,
    };
  }

  private mapWorkflowStage(raw: unknown, index: number): WorkflowStageApiContract {
    const source = asRecord(raw);
    const stageData = asRecord(source['stage']);
    const id =
      readEntityId(source) ||
      readEntityId(stageData) ||
      firstNonEmptyString(
        source['workflowStageId'],
        source['WorkflowStageId'],
        source['stageId'],
        source['StageId']
      );
    const name = firstNonEmptyString(
      source['name'],
      source['Name'],
      source['title'],
      source['Title'],
      source['nome'],
      source['Nome'],
      stageData['name'],
      stageData['Name'],
      stageData['title'],
      stageData['Title'],
      stageData['nome'],
      stageData['Nome'],
      id,
      `stage-${index + 1}`
    );
    return {
      id,
      name,
      order: asNumber(source['order'], asNumber(stageData['order'], index + 1)),
      colorHex: asString(source['colorHex']) || asString(stageData['colorHex'], '#94A3B8'),
    };
  }

  private mapCard(raw: unknown): CardApiContract {
    const source = unwrapCardPayload(raw);
    const assignedTo = asRecord(source['assignedTo'] ?? source['AssignedTo']);
    const ownerObj = asRecord(source['owner'] ?? source['Owner']);
    const ownerUserObj = asRecord(source['ownerUser'] ?? source['OwnerUser']);
    const ownerUserId = firstNonEmptyString(
      source['ownerUserId'],
      source['OwnerUserId'],
      source['assigneeUserId'],
      source['AssigneeUserId'],
      source['ownerId'],
      source['OwnerId'],
      source['assignedToUserId'],
      source['AssignedToUserId'],
      source['assignedUserId'],
      source['AssignedUserId'],
      assignedTo['userId'],
      assignedTo['UserId'],
      assignedTo['id'],
      assignedTo['Id'],
      ownerObj['userId'],
      ownerObj['UserId'],
      ownerObj['id'],
      ownerObj['Id'],
      ownerUserObj['id'],
      ownerUserObj['Id']
    );
    const owner = firstNonEmptyString(
      source['ownerName'],
      source['OwnerName'],
      source['owner'],
      source['responsibleName'],
      source['assigneeName'],
      source['ownerUserName'],
      source['assignedToName'],
      source['AssignedToName'],
      ownerUserObj['name'],
      ownerUserObj['Name'],
      ownerUserObj['fullName'],
      ownerUserObj['FullName'],
      ownerUserObj['displayName'],
      ownerUserObj['DisplayName'],
      asRecord(source['ownerProfile'])['name'],
      asRecord(source['ownerProfile'])['displayName'],
      assignedTo['name'],
      assignedTo['Name'],
      assignedTo['displayName'],
      assignedTo['DisplayName'],
      assignedTo['fullName'],
      assignedTo['FullName'],
      ownerObj['name'],
      ownerObj['Name'],
      ownerObj['displayName'],
      ownerObj['DisplayName'],
      ownerObj['fullName'],
      ownerObj['FullName']
    );
    const rawPriority = firstNonEmptyString(
      source['priority'],
      source['cardPriority'],
      source['priorityName']
    ).toLowerCase();
    const positionRaw = source['position'] ?? source['Position'];
    let position: number | null = null;
    if (typeof positionRaw === 'number' && Number.isFinite(positionRaw)) {
      position = Math.trunc(positionRaw);
    } else if (typeof positionRaw === 'string' && /^-?\d+$/.test(positionRaw.trim())) {
      position = Math.trunc(Number(positionRaw.trim()));
    }
    const quickFields = readQuickFieldsArray(source).map((item) => this.mapCardQuickField(item));
    const fieldValues = extractCardFieldValues(source);
    /**
     * Fallback: se o backend só enviou os valores em `quickFields`, ainda assim alimentamos
     * `fieldValues` para que a lista não dependa do filtro de quick view.
     */
    for (const qf of quickFields) {
      if (qf.value == null) continue;
      for (const id of [qf.workflowFieldId, qf.key, qf.label]) {
        const k = (id ?? '').trim();
        if (!k) continue;
        if (fieldValues[k] == null) fieldValues[k] = qf.value;
      }
    }

    let description = firstNonEmptyString(
      source['description'],
      source['Description'],
      source['details'],
      source['Details'],
      source['body'],
      source['Body'],
      source['descricao'],
      source['Descricao'],
      source['cardDescription'],
      source['CardDescription'],
      source['summary'],
      source['Summary'],
      source['notes'],
      source['Notes'],
      source['longDescription'],
      source['LongDescription']
    );
    if (!description.length) {
      description = this.pickDescriptionFromQuickFields(quickFields);
    }
    if (!description.length) {
      description = this.pickDescriptionFromCustomMaps(source);
    }

    const codeRaw = source['code'] ?? source['Code'];
    let code: string | null = null;
    if (typeof codeRaw === 'number' && Number.isFinite(codeRaw)) {
      code = String(Math.trunc(codeRaw));
    } else if (typeof codeRaw === 'string') {
      const tc = codeRaw.trim();
      code = tc.length ? tc : null;
    }

    return {
      id: readEntityId(source) || asString(source['id']),
      code,
      projectId: firstNonEmptyString(
        source['projectId'],
        source['ProjectId'],
        asRecord(source['project'])['id'],
        asRecord(source['project'])['Id']
      ),
      workflowId: firstNonEmptyString(
        source['workflowId'],
        source['WorkflowId'],
        asRecord(source['workflow'])['id'],
        asRecord(source['workflow'])['Id']
      ),
      workflowStageId: firstNonEmptyString(
        source['workflowStageId'],
        source['WorkflowStageId'],
        source['stageId'],
        source['StageId'],
        asRecord(source['workflowStage'])['id'],
        asRecord(source['workflowStage'])['Id']
      ),
      position,
      createdAt: asNullableString(
        source['createdAt'] ??
          source['CreatedAt'] ??
          source['createdOn'] ??
          source['CreatedOn']
      ),
      title: asString(source['title']),
      description: description.length ? description : null,
      latestComment: asNullableString(source['latestComment'] ?? source['LatestComment']),
      ownerUserId: ownerUserId || null,
      /** Sem fallback fictício: detalhe do card resolve nome via ownerUserId + opções de utilizador. */
      ownerName: owner,
      priority:
        rawPriority === 'alta' || rawPriority === 'high'
          ? 'Alta'
          : rawPriority === 'baixa' || rawPriority === 'low'
            ? 'Baixa'
            : rawPriority
              ? 'Média'
              : null,
      dueDate: asNullableString(source['dueDate']),
      commentsCount: asNumber(source['commentsCount']),
      attachmentsCount: asNumber(source['attachmentsCount']),
      checklistDone: asNumber(source['checklistDone']),
      checklistTotal: asNumber(source['checklistTotal']),
      quickFields,
      fieldValues,
    };
  }

  /**
   * Listagens por workflow costumam omitir `description` no root; o mesmo texto pode vir em quickFields
   * (ex.: key `native:description`), igual ao detalhe do card.
   */
  private pickDescriptionFromQuickFields(qfs: CardQuickFieldApiContract[]): string {
    for (const qf of qfs) {
      if (!this.quickFieldIsNativeDescription(qf)) continue;
      const raw = qf.value;
      if (raw == null || typeof raw === 'boolean') continue;
      const s = String(raw).trim();
      if (s.length) return s;
    }
    return '';
  }

  private quickFieldIsNativeDescription(qf: CardQuickFieldApiContract): boolean {
    /** Alinhado a `workflow-forms-api`: 10002 = campo nativo descrição do card. */
    if (qf.fieldType === 10002) return true;
    const k = normalizeAsciiLower(qf.key);
    const wid = normalizeAsciiLower(qf.workflowFieldId);
    const lbl = normalizeAsciiLower(qf.label);
    if (
      k === 'native:description' ||
      k === 'description' ||
      k === 'carddescription' ||
      k === 'descricao'
    ) {
      return true;
    }
    if (k.startsWith('native:') && (k.endsWith('description') || k.endsWith('descricao'))) {
      return true;
    }
    if (wid === 'native:description') return true;
    /** Listagem por workflow às vezes só envia label amigável, sem key nativa. */
    if (lbl === 'description' || lbl === 'descricao') return true;
    return false;
  }

  /**
   * Descrição pode vir só em mapas de valores (sem quickFields), ex.: `customFields["native:description"]`.
   */
  private pickDescriptionFromCustomMaps(source: JsonRecord): string {
    const bagKeys = [
      'customFields',
      'CustomFields',
      'customFieldValues',
      'CustomFieldValues',
      'workflowFieldValues',
      'WorkflowFieldValues',
    ] as const;
    const nativeKeys = [
      'native:description',
      'Native:Description',
      'description',
      'Description',
      'cardDescription',
      'CardDescription',
      'descricao',
      'Descricao',
    ];
    for (const bk of bagKeys) {
      const bag = source[bk];
      if (!bag || typeof bag !== 'object' || Array.isArray(bag)) continue;
      const rec = bag as JsonRecord;
      for (const nk of nativeKeys) {
        const coerced = coerceQuickFieldScalar(rec[nk]);
        if (coerced == null || typeof coerced === 'boolean') continue;
        const s = String(coerced).trim();
        if (s.length) return s;
      }
    }

    const arrayKeys = ['fieldValues', 'FieldValues', 'cardFieldValues', 'CardFieldValues'] as const;
    for (const ak of arrayKeys) {
      const arr = source[ak];
      if (!Array.isArray(arr)) continue;
      for (const item of arr) {
        const r = asRecord(item);
        const keyHint = normalizeAsciiLower(
          firstNonEmptyString(r['key'], r['Key'], r['fieldKey'], r['FieldKey'], '')
        );
        const wid = normalizeAsciiLower(
          firstNonEmptyString(r['workflowFieldId'], r['WorkflowFieldId'], r['fieldId'], r['FieldId'], '')
        );
        const lbl = normalizeAsciiLower(firstNonEmptyString(r['label'], r['Label'], ''));
        const val = coerceQuickFieldScalar(r['value'] ?? r['Value']);
        if (val == null || typeof val === 'boolean') continue;
        const text = String(val).trim();
        if (!text.length) continue;
        const ftRaw = r['fieldType'] ?? r['FieldType'];
        const ftn = typeof ftRaw === 'number' ? ftRaw : Number(String(ftRaw).trim());
        const matchesKey =
          keyHint === 'native:description' ||
          keyHint === 'description' ||
          keyHint === 'descricao' ||
          keyHint === 'carddescription' ||
          (keyHint.startsWith('native:') && keyHint.endsWith('description')) ||
          wid === 'native:description';
        const matchesLabel = lbl === 'description' || lbl === 'descricao';
        if (matchesKey || matchesLabel || ftn === 10002) return text;
      }
    }
    return '';
  }

  private mapCardQuickField(raw: unknown): CardQuickFieldApiContract {
    const source = asRecord(raw);
    const rawValue = source['value'] ?? source['Value'];
    const wfId = firstNonEmptyString(
      source['workflowFieldId'],
      source['WorkflowFieldId'],
      source['fieldId'],
      source['FieldId']
    );
    const key = firstNonEmptyString(source['key'], source['Key']);
    const label = firstNonEmptyString(source['label'], source['Label']);
    const ft = source['fieldType'] ?? source['FieldType'];
    let fieldType = 0;
    if (typeof ft === 'number' && Number.isFinite(ft)) {
      fieldType = Math.trunc(ft);
    } else if (typeof ft === 'string' && ft.trim()) {
      const n = Number(ft.trim());
      fieldType = Number.isFinite(n) ? Math.trunc(n) : 0;
    }
    return {
      workflowFieldId: wfId,
      key,
      label,
      fieldType,
      value: coerceQuickFieldScalar(rawValue),
      colorHex: asNullableString(source['colorHex']) ?? asNullableString(source['ColorHex']),
    };
  }

  private async getWithFallback(endpoints: string[]): Promise<unknown> {
    let lastError: unknown;
    for (const endpoint of endpoints) {
      try {
        return await firstValueFrom(this.http.get<unknown>(endpoint));
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError ?? new Error('Request failed.');
  }
}
